
import React from "react";
import { Button } from "@/components/ui/button";
import { Check, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { getDialCodeForCountry } from "@/lib/phoneNumber";
import { msg } from "@lingui/macro";
import { useLingui } from "@lingui/react";

export type CountryMode = "country" | "dialCode";

const COUNTRIES = [
  { code: 'US', name: 'United States', flag: '🇺🇸' , dialCode: '+1' },
  { code: 'CA', name: 'Canada', flag: '🇨🇦' , dialCode: '+1' },
  { code: 'MX', name: 'Mexico', flag: '🇲🇽' , dialCode: '+52' },
  { code: 'GB', name: 'United Kingdom', flag: '🇬🇧' , dialCode: '+44' },
  { code: 'DE', name: 'Germany', flag: '🇩🇪' , dialCode: '+49' },
  { code: 'FR', name: 'France', flag: '🇫🇷' , dialCode: '+33' },
  { code: 'ES', name: 'Spain', flag: '🇪🇸' , dialCode: '+34' },
  { code: 'IT', name: 'Italy', flag: '🇮🇹' , dialCode: '+39' },
  { code: 'NL', name: 'Netherlands', flag: '🇳🇱' , dialCode: '+31' },
  { code: 'BE', name: 'Belgium', flag: '🇧🇪' , dialCode: '+32' },
  { code: 'CH', name: 'Switzerland', flag: '🇨🇭' , dialCode: '+41' },
  { code: 'AT', name: 'Austria', flag: '🇦🇹' , dialCode: '+43' },
  { code: 'SE', name: 'Sweden', flag: '🇸🇪' , dialCode: '+46' },
  { code: 'NO', name: 'Norway', flag: '🇳🇴' , dialCode: '+47' },
  { code: 'DK', name: 'Denmark', flag: '🇩🇰' , dialCode: '+45' },
  { code: 'FI', name: 'Finland', flag: '🇫🇮' , dialCode: '+358' },
  { code: 'IE', name: 'Ireland', flag: '🇮🇪' , dialCode: '+353' },
  { code: 'PT', name: 'Portugal', flag: '🇵🇹' , dialCode: '+351' },
  { code: 'GR', name: 'Greece', flag: '🇬🇷' , dialCode: '+30' },
  { code: 'PL', name: 'Poland', flag: '🇵🇱' , dialCode: '+48' },
  { code: 'CZ', name: 'Czech Republic', flag: '🇨🇿' , dialCode: '+420' },
  { code: 'HU', name: 'Hungary', flag: '🇭🇺' , dialCode: '+36' },
  { code: 'SK', name: 'Slovakia', flag: '🇸🇰' , dialCode: '+421' },
  { code: 'SI', name: 'Slovenia', flag: '🇸🇮' , dialCode: '+386' },
  { code: 'HR', name: 'Croatia', flag: '🇭🇷' , dialCode: '+385' },
  { code: 'BG', name: 'Bulgaria', flag: '🇧🇬' , dialCode: '+359' },
  { code: 'RO', name: 'Romania', flag: '🇷🇴' , dialCode: '+40' },
  { code: 'EE', name: 'Estonia', flag: '🇪🇪' , dialCode: '+372' },
  { code: 'LV', name: 'Latvia', flag: '🇱🇻' , dialCode: '+371' },
  { code: 'LT', name: 'Lithuania', flag: '🇱🇹' , dialCode: '+370' },
  { code: 'LU', name: 'Luxembourg', flag: '🇱🇺' , dialCode: '+352' },
  { code: 'MT', name: 'Malta', flag: '🇲🇹' , dialCode: '+356' },
  { code: 'CY', name: 'Cyprus', flag: '🇨🇾' , dialCode: '+357' },
  { code: 'BR', name: 'Brazil', flag: '🇧🇷' , dialCode: '+55' },
  { code: 'AR', name: 'Argentina', flag: '🇦🇷' , dialCode: '+54' },
  { code: 'CO', name: 'Colombia', flag: '🇨🇴' , dialCode: '+57' },
  { code: 'PE', name: 'Peru', flag: '🇵🇪' , dialCode: '+51' },
  { code: 'CL', name: 'Chile', flag: '🇨🇱' , dialCode: '+56' },
  { code: 'VE', name: 'Venezuela', flag: '🇻🇪' , dialCode: '+58' },
  { code: 'UY', name: 'Uruguay', flag: '🇺🇾' , dialCode: '+598' },
  { code: 'PY', name: 'Paraguay', flag: '🇵🇾' , dialCode: '+595' },
  { code: 'BO', name: 'Bolivia', flag: '🇧🇴' , dialCode: '+591' },
  { code: 'EC', name: 'Ecuador', flag: '🇪🇨' , dialCode: '+593' },
  { code: 'GT', name: 'Guatemala', flag: '🇬🇹' , dialCode: '+502' },
  { code: 'BZ', name: 'Belize', flag: '🇧🇿' , dialCode: '+501' },
  { code: 'HN', name: 'Honduras', flag: '🇭🇳' , dialCode: '+504' },
  { code: 'SV', name: 'El Salvador', flag: '🇸🇻' , dialCode: '+503' },
  { code: 'NI', name: 'Nicaragua', flag: '🇳🇮' , dialCode: '+505' },
  { code: 'CR', name: 'Costa Rica', flag: '🇨🇷' , dialCode: '+506' },
  { code: 'PA', name: 'Panama', flag: '🇵🇦' , dialCode: '+507' },
  { code: 'CU', name: 'Cuba', flag: '🇨🇺' , dialCode: '+53' },
  { code: 'DO', name: 'Dominican Republic', flag: '🇩🇴' , dialCode: '+1809' },
  { code: 'HT', name: 'Haiti', flag: '🇭🇹' , dialCode: '+509' },
  { code: 'JM', name: 'Jamaica', flag: '🇯🇲' , dialCode: '+1876' },
  { code: 'TT', name: 'Trinidad and Tobago', flag: '🇹🇹' , dialCode: '+1868' },
  { code: 'BB', name: 'Barbados', flag: '🇧🇧' , dialCode: '+1246' },
  { code: 'BS', name: 'Bahamas', flag: '🇧🇸' , dialCode: '+1242' },
  { code: 'GY', name: 'Guyana', flag: '🇬🇾' , dialCode: '+592' },
  { code: 'SR', name: 'Suriname', flag: '🇸🇷' , dialCode: '+597' },
  { code: 'GF', name: 'French Guiana', flag: '🇬🇫' , dialCode: '+594' },
  { code: 'AW', name: 'Aruba', flag: '🇦🇼' , dialCode: '+297' },
  { code: 'CW', name: 'Curaçao', flag: '🇨🇼' , dialCode: '+599' },
  { code: 'SX', name: 'Sint Maarten', flag: '🇸🇽' , dialCode: '+1721' },
  { code: 'BQ', name: 'Bonaire', flag: '🇧🇶' , dialCode: '+599' },
  { code: 'GP', name: 'Guadeloupe', flag: '🇬🇵' , dialCode: '+590' },
  { code: 'MQ', name: 'Martinique', flag: '🇲🇶' , dialCode: '+596' },
  { code: 'PR', name: 'Puerto Rico', flag: '🇵🇷' , dialCode: '+1787' },
  { code: 'VI', name: 'U.S. Virgin Islands', flag: '🇻🇮' , dialCode: '+1340' },
  { code: 'VG', name: 'British Virgin Islands', flag: '🇻🇬' , dialCode: '+1284' },
  { code: 'KY', name: 'Cayman Islands', flag: '🇰🇾' , dialCode: '+1345' },
  { code: 'TC', name: 'Turks and Caicos Islands', flag: '🇹🇨' , dialCode: '+1649' },
  { code: 'AG', name: 'Antigua and Barbuda', flag: '🇦🇬' , dialCode: '+1268' },
  { code: 'DM', name: 'Dominica', flag: '🇩🇲' , dialCode: '+1767' },
  { code: 'GD', name: 'Grenada', flag: '🇬🇩' , dialCode: '+1473' },
  { code: 'LC', name: 'Saint Lucia', flag: '🇱🇨' , dialCode: '+1758' },
  { code: 'VC', name: 'Saint Vincent and the Grenadines', flag: '🇻🇨' , dialCode: '+1784' },
  { code: 'KN', name: 'Saint Kitts and Nevis', flag: '🇰🇳' , dialCode: '+1869' },
  { code: 'AU', name: 'Australia', flag: '🇦🇺' , dialCode: '+61' },
  { code: 'NZ', name: 'New Zealand', flag: '🇳🇿' , dialCode: '+64' },
  { code: 'JP', name: 'Japan', flag: '🇯🇵' , dialCode: '+81' },
  { code: 'KR', name: 'South Korea', flag: '🇰🇷' , dialCode: '+82' },
  { code: 'CN', name: 'China', flag: '🇨🇳' , dialCode: '+86' },
  { code: 'IN', name: 'India', flag: '🇮🇳' , dialCode: '+91' },
  { code: 'SG', name: 'Singapore', flag: '🇸🇬' , dialCode: '+65' },
  { code: 'HK', name: 'Hong Kong', flag: '🇭🇰' , dialCode: '+852' },
  { code: 'TW', name: 'Taiwan', flag: '🇹🇼' , dialCode: '+886' },
  { code: 'MY', name: 'Malaysia', flag: '🇲🇾' , dialCode: '+60' },
  { code: 'TH', name: 'Thailand', flag: '🇹🇭' , dialCode: '+66' },
  { code: 'PH', name: 'Philippines', flag: '🇵🇭' , dialCode: '+63' },
  { code: 'ID', name: 'Indonesia', flag: '🇮🇩' , dialCode: '+62' },
  { code: 'VN', name: 'Vietnam', flag: '🇻🇳' , dialCode: '+84' },
  { code: 'ZA', name: 'South Africa', flag: '🇿🇦' , dialCode: '+0' },
  { code: 'EG', name: 'Egypt', flag: '🇪🇬' , dialCode: '+0' },
  { code: 'MA', name: 'Morocco', flag: '🇲🇦' , dialCode: '+0' },
  { code: 'NG', name: 'Nigeria', flag: '🇳🇬' , dialCode: '+0' },
  { code: 'KE', name: 'Kenya', flag: '🇰🇪' , dialCode: '+0' },
  { code: 'GH', name: 'Ghana', flag: '🇬🇭' , dialCode: '+0' },
  { code: 'IL', name: 'Israel', flag: '🇮🇱' , dialCode: '+0' },
  { code: 'AE', name: 'United Arab Emirates', flag: '🇦🇪' , dialCode: '+0' },
  { code: 'SA', name: 'Saudi Arabia', flag: '🇸🇦' , dialCode: '+0' },
  { code: 'TR', name: 'Turkey', flag: '🇹🇷' , dialCode: '+0' },
  { code: 'RU', name: 'Russia', flag: '🇷🇺' , dialCode: '+0' },
  { code: 'UA', name: 'Ukraine', flag: '🇺🇦' , dialCode: '+0' },
] as const;

interface CountrySelectProps {
  value?: string;
  onValueChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  mode?: CountryMode;
  /** Applied to the outer wrapper div (e.g. "mt-1") */
  className?: string;
  /** Applied to the trigger button (e.g. "h-14") */
  buttonClassName?: string;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
}

export function CountrySelect({
  value,
  onValueChange,
  placeholder = "Select country",
  disabled = false,
  mode = "country",
  className,
  buttonClassName,
  ...ariaProps
}: CountrySelectProps) {
  const { i18n } = useLingui();

  const [open, setOpen] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const searchInputRef = React.useRef<HTMLInputElement | null>(null);
  const [search, setSearch] = React.useState("");

  React.useEffect(() => {
    if (!open) return;
    if (typeof window === "undefined") return;

    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null;
      if (containerRef.current && target && !containerRef.current.contains(target)) {
        setOpen(false);
      }
    };

    window.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("touchstart", handleClickOutside);

    return () => {
      window.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("touchstart", handleClickOutside);
    };
  }, [open]);

  React.useEffect(() => {
    if (open && searchInputRef.current) {
      try {
        searchInputRef.current.focus();
      } catch {
        // ignore focus errors
      }
    }
  }, [open]);

  const selectedCountry = COUNTRIES.find((country) => country.code === value);

  const filteredCountries = React.useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return COUNTRIES;
    return COUNTRIES.filter(
      (country) =>
        country.name.toLowerCase().includes(term) ||
        country.code.toLowerCase().includes(term),
    );
  }, [search]);

  return (
    <div
      ref={containerRef}
      className={cn("relative w-full", className)}
      onClick={() => setOpen((prev) => !prev)}
    >
      <Button
        variant="outline"
        role="combobox"
        aria-expanded={open}
        className={cn(
          "w-full justify-between text-base border-2 border-gray-200 bg-white hover:bg-white focus:border-[#166B93] focus:ring-[#166B93]/20 transition-all duration-200 rounded-xl h-12 !mb-0",
          buttonClassName
        )}
        disabled={disabled}
        {...ariaProps}
      >
        <div className="flex flex-1 items-center gap-2 min-w-0">
          {selectedCountry ? (
            <>
              <span className="text-lg">{selectedCountry.flag}</span>
              <span className={mode === "dialCode" ? "font-medium text-sm" : "truncate"}>
                {mode === "dialCode" ? (getDialCodeForCountry(selectedCountry.code) ?? selectedCountry.dialCode) : selectedCountry.name}
              </span>
            </>
          ) : (
            <span className="text-muted-foreground">{placeholder}</span>
          )}
        </div>
        <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
      </Button>
      {open && (
        <div className={cn("country-dropdown absolute top-full mt-1 z-50 rounded-xl border border-gray-200 bg-white shadow-lg shadow-black/5", mode === "dialCode" ? "left-0 w-[min(300px,calc(100vw-2rem))]" : "inset-x-0 w-full max-w-full")} onClick={(e) => e.stopPropagation()}>
          <div className="p-2 border-b border-gray-100">
            <input
              ref={searchInputRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={i18n._(msg`Search countries...`)}
              className="w-full h-12 rounded-md border border-gray-200 px-3 text-base outline-none focus:border-[#166B93] focus:ring-2 focus:ring-[#166B93]/20"
            />
          </div>
          <div className="max-h-64 overflow-y-auto">
            {filteredCountries.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-600">
                {i18n._(msg`No country found.`)}
              </div>
            ) : (
              filteredCountries.map((country) => (
                <button
                  key={country.code}
                  type="button"
                  onClick={() => {
                    onValueChange?.(country.code);
                    setOpen(false);
                    setSearch("");
                  }}
                  className={cn(
                    "flex w-full items-center gap-3 px-3 py-2 text-left text-sm hover:bg-gray-100 !mb-0 !min-h-0 leading-normal",
                    value === country.code ? "bg-gray-100" : "",
                  )}
                >
                  <span className="text-lg">{country.flag}</span>
                  <span className="flex-1 truncate">{country.name}</span>
                  {mode === "dialCode" && (
                    <span className="text-gray-500 font-medium ml-1 shrink-0">{getDialCodeForCountry(country.code) ?? country.dialCode}</span>
                  )}
                  <Check
                    className={cn(
                      "ml-auto h-4 w-4",
                      value === country.code ? "opacity-100" : "opacity-0",
                    )}
                  />
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export { COUNTRIES };